/**
 * Auth Store
 * --------------------
 * Pinia store to manage authentication state, user data, 
 * account information, roles, and package features.
 *
 * @author  Harvinder Singh <harvinder@maropost.com>
 * @created 2025-09-12
 */

import { defineStore } from "pinia"
import keycloak from "../plugins/keycloak-config"

export const useUserStore = defineStore("user-store", {
  // -----------------------------
  // State
  // -----------------------------
  state: () => ({
    currentApp: (null as string | null),
    auth: {
      account: {
        id: 0,
        current: {} as any,
      },
      userdata: {
        email: "",
        firstName: "",
        lastName: "",
        username: "",
      },
      preference: {
        default_account_id: "",
        time_zone: "America/New_York",
      },
      productAccess: {
        marketing: false,
        service: false,
        commerce: false,
        mhc: false,
        neto: false,
      },
      roles: [] as any[],
      productRoles: {
        marketing: false,
        service: false,
        commerce: false
      },
      packageFeatures: [] as any,
      trailPackageFeatures: {} ,
    },
    accounts: [] as any[],
    netoStores: [] as any[],
    mhcStores: [] as any[],
    saleNavigation: false,
    isNavigationRail: true,
  }),

  // -----------------------------
  // Getters
  // -----------------------------
  getters: {
    /** Get currently active account ID */
    getAccountId: (state): number => state.auth.account.id,

    /** Get full username */
    username(): string {
      const { firstName, lastName } = this.auth.userdata
      return `${firstName} ${lastName}`.trim()
    },

    /** Get user data with fallback */
    userdata(): { email: string } {
      const { email = "unknown" } = this.auth.userdata
      return { email }
    },

    /** Get all active accounts */
    getActiveAccounts: (state): object[] => state.accounts || [],

    /** Get default account ID from preferences */
    getDefaultAccountId: (state): number =>
      parseInt(state.auth?.preference?.default_account_id || "0"),

    /** Get default account */
    getDefaultAccount: (state): any => state.auth.account.current || {},
    
    /** Get all roles */
    getRoles: (state): any[] => state.auth.roles || [],

    /** Get package features */
    getPackageFeatures: (state): Array<any> => state.auth.packageFeatures || [],

    /** Get trail package features */
    getTrailPackageFeatures: (state): object => state.auth.trailPackageFeatures || {},

    /** Get product access on roles */
    getProductAccessOnRoles: (state) => {
      return (productName: string): boolean => {
        const key = productName.trim().toLowerCase()
        const roleValue =
        state.auth.productRoles[
          key as keyof typeof state.auth.productRoles
        ];
      
        if (roleValue !== undefined) {
          return roleValue;
        }
        
        const accessValue =
          state.auth.productAccess[
            key as keyof typeof state.auth.productAccess
          ];
        
        if (accessValue !== undefined) {
          return accessValue;
        }
        
        return false;
      }
    },
    /** Get all product access flags */
    getAllProductAccess: (state): {
      marketing: boolean
      service: boolean
      commerce: boolean
    } => state.auth.productAccess,

    /**
     * Get access flag for a single product by name (case-insensitive).
     *
     * Example:
     *   store.getProductAccess("marketing") -> true/false
     */
    getProductAccess: (state) => {
      return (productName: string): boolean => {
        const key = productName.trim().toLowerCase()
        return (
          state.auth.productAccess[
            key as keyof typeof state.auth.productAccess
          ] || false
        )
      }
    },

    /** Check if user has ANY product access
     * except Neto and Findify
     */
    hasAnyProductAccess: (state): boolean =>
      Object.values(state.auth.productAccess).some((access) => access),

    /** Check if user has ALL product access */
    hasAllProductAccess: (state): boolean =>
      Object.values(state.auth.productAccess).every((access) => access),

    /** Get neto stores */
    getNetoStores: (state): any[] => state.netoStores || [],

    /** Get mhc stores */
    getMhcStores: (state): any[] => state.mhcStores || [],

    /** Get sale navigation */
    getSaleNavigation: (state): boolean => state.saleNavigation,

    /** Get navigation rail */
    getNavigationRail: (state): boolean => state.isNavigationRail,
  },

  // -----------------------------
  // Actions
  // -----------------------------
  actions: {
    /** Set account ID */
    setAccountId(value: number) {
      this.auth.account.id = value
    },

    /** Set navigation rail */
    setNavigationRail(value: boolean): void {
      this.isNavigationRail = value
      window.dispatchEvent(
        new CustomEvent("shell:sidebar-transition-end", {
          
          detail: value,
        })
      )
    },

    /** Logout via Keycloak and reset store state */
    async logout() {
      const { onBoardingService } = await import("@/lib/services/onBoardingService")
      onBoardingService.reset()
      this.$reset()
      localStorage.removeItem("auth-store")
      keycloak.logout()
    },

    /** Set available accounts */
    setAccounts(value: any[]) {
      this.accounts = value
    },

    /** Set neto stores */
    setNetoStores(value: any[]) {
      this.netoStores = value
    },

    /** Set mhc stores */
    setMhcStores(value: any[]) {
      this.mhcStores = value
    },

    /** Set sale navigation */
    setSaleNavigation(value: boolean) {
      this.saleNavigation = value
    },

    /** Set the current default account */
    setDefaultAccount(account: object = {}) {
      this.auth.account.current = account
    },

    /** Set user roles */
    setRoles(roles: any) {
      // Assign roles safely (ensure it's always an array)
      this.auth.roles = Array.isArray(roles?.roles)
      ? [...roles.roles]
      : [];

      // 🔍 Validate mobile role after setting roles
      const isMobileAllowed = this.checkRole("mobile");

      // If mobile is NOT allowed → remove it
      if (!isMobileAllowed) {
        this.auth.roles = this.auth.roles.filter(
          role => role !== "mobile"
        );
      }
      const hasServiceAccess = !!roles.roles.some((role:any) => role === "service" || role.startsWith("service/"));
      // Check if the user has "marketing" access
      const hasMarketingAccess = !!roles.roles.find((role:any) =>
         role !== "contacts" && role !== "download_contacts" && role !== "account" && role !== "accounts_read_only" && role !== "admin" && role !== "user" && role !== "segments" && !role.startsWith("service") && !role.startsWith("service/")
      );
      this.auth.productRoles.service = hasServiceAccess
      this.auth.productRoles.marketing = hasMarketingAccess
      this.auth.productRoles.commerce = this.auth.productAccess.commerce
    },

    /** Set package features */
    setPackageFeatures(features: Array<any> = []) {
      this.auth.packageFeatures = features
    },

    /** Set trail package features */
    setTrailPackageFeatures(features : any) {
      this.auth.trailPackageFeatures = features
    },

    /** Bulk set product access */
    setProductAccess(product: any = {}) {
      this.auth.productAccess.marketing = product?.enable_marketing || false
      this.auth.productAccess.service = product?.enable_service || false
      this.auth.productAccess.commerce = product?.enable_commerce || false
    },

    /** Update access flag for a single product */
    setSingleProductAccess(productName: string, value: boolean) {
      const key = productName.trim().toLowerCase()
      if (!(key in this.auth.productAccess)) {
        console.warn(`[userStore] Unknown product "${productName}"`)
        return
      }
      this.auth.productAccess[
        key as keyof typeof this.auth.productAccess
      ] = value
    },

    /** Check if a role is allowed based on roles and package features */
    checkRole(role: string): boolean {
      if (role === "uid") {
        return !this.auth.account?.current?.settings?.account_settings
          ?.customer_preferred_uid
      }
      if (role === "mobile") {
        return (
          this.auth.roles.includes(role) &&
          this.auth.account?.current?.settings?.account_informations?.sms_features?.from_number
        )
      }
      return this.auth.roles.includes(role)
    },

    /** Fetch and set user data from Keycloak */
    async getUserData() {
      const profile = await keycloak.loadUserProfile()
      this.auth.userdata = {
        email: profile.email || "",
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        username: profile.username || "",
      }

      if (profile?.attributes?.preferences?.[0]) {
        this.auth.preference = JSON.parse(profile.attributes.preferences[0])
      }
    },

  },

  // -----------------------------
  // Persistence Config
  // -----------------------------
  persist: true
})
